import { useState, useMemo, useCallback } from "react";
import { loadComponentSelections, type ComponentSelections } from "../utils/componentSelections";

function computeSignature(selections: ComponentSelections | null): string | null {
	if (!selections) return null;
	return JSON.stringify(selections);
}

export interface UseInitializationSourceOptions {
	configBundleId: string;
	initialSelections: ComponentSelections | null;
	isUrlLoading?: boolean;
	allowStoredSelectionsFallback?: boolean;
}

export interface UseInitializationSourceReturn {
	selectionsToApply: ComponentSelections | null;
	shouldApply: boolean;
	isInitialized: boolean;
	markApplied: () => void;
	reset: () => void;
}

export function useInitializationSource({
	configBundleId,
	initialSelections,
	isUrlLoading = false,
	allowStoredSelectionsFallback = true,
}: UseInitializationSourceOptions): UseInitializationSourceReturn {
	const [applied, setApplied] = useState(false);
	const [ignoredSignature, setIgnoredSignature] = useState<string | null>(null);

	const urlSignature = useMemo(
		() => computeSignature(initialSelections),
		[initialSelections]
	);

	const hasUrlConfiguration = useMemo(
		() => !!urlSignature && urlSignature !== ignoredSignature,
		[urlSignature, ignoredSignature]
	);

	const selectionsToApply = useMemo((): ComponentSelections | null => {
		if (applied) return null;
		if (!configBundleId) return null;

		if (hasUrlConfiguration && initialSelections) {
			return initialSelections;
		}

		if (!allowStoredSelectionsFallback) {
			return null;
		}

		const storedSelections = loadComponentSelections(configBundleId);
		if (Object.keys(storedSelections).length > 0) {
			return storedSelections;
		}

		return null;
	}, [applied, configBundleId, hasUrlConfiguration, initialSelections, allowStoredSelectionsFallback]);

	const shouldApply = !applied && !!configBundleId && !isUrlLoading;

	const markApplied = useCallback(() => {
		if (!configBundleId) {
			return;
		}

		setApplied(true);
	}, [configBundleId]);

	const reset = useCallback(() => {
		setIgnoredSignature(urlSignature);
		setApplied(true);
	}, [urlSignature]);

	return {
		selectionsToApply,
		shouldApply,
		isInitialized: applied,
		markApplied,
		reset,
	};
}